/**
 * The faint topographic rings behind the cover band — `docs/proposal.md` §6.2.
 *
 * The mockups draw twelve concentric contour outlines in `--contour` over the
 * brand band, each a closed 62-point polyline wobbling around a flattened
 * ellipse off to the right. **They are generated here rather than pasted**,
 * which is the one place this module departs from the `mark-glyphs.tsx`
 * pattern of lifting a mockup's paths as data, and for a reason the boards
 * show themselves: the band is drawn at 390, 820, 1180 and 1280+ and the
 * mockup's own `preserveAspectRatio="none"` would squash a fixed 1280 × 380
 * path set by 3.3× on a phone — which is exactly why `cp-08-phone.html`
 * carries a *second*, hand-made set at 390 × 236. Two files of paths that have
 * to agree about a decoration is two files that will not; one function that
 * takes the box is the same picture at every width.
 *
 * It is pure string arithmetic over numbers, so it renders real `<path>`
 * elements and nothing reaches `dangerouslySetInnerHTML`, and
 * `tests/components/proposal-contours.test.ts` holds it to being deterministic
 * and well-formed.
 */

export interface ContourRing {
  /** An SVG path, closed, in the coordinate space of the box passed in. */
  d: string
  opacity: number
}

/** How many rings, and where they sit — measured off `cp-01-proposal-top.html`. */
const RINGS = 12
/** The focus, as a fraction of the box: right of centre and above the middle. */
const FOCUS_X = 0.8
const FOCUS_Y = 0.42
/** The innermost ring's radius, as a fraction of the width, and the growth. */
const FIRST = 0.0727
const GROWTH = 1.237
/** The rings are flattened: the mockup's are about twice as wide as they are tall. */
const FLATTEN = 2.12
const POINTS = 62

/**
 * A ring's wobble, as a fraction of its own radius.
 *
 * It grows outward: the innermost ring is nearly an ellipse and the outermost
 * wanders, which is what makes the set read as land rather than as a target.
 */
const wobbleOf = (ring: number): number => 0.045 + 0.006 * ring

/**
 * Three sines, phased per ring.
 *
 * Deterministic and seedless — the ring index *is* the seed, so the same box
 * always draws the same rings and a re-render never shimmers.
 */
function noise(ring: number, angle: number): number {
  const phase = ring * 1.7
  return (
    Math.sin(angle * 3 + phase) * 0.55
    + Math.sin(angle * 5 - phase * 1.3) * 0.3
    + Math.sin(angle * 8 + phase * 0.7) * 0.15
  )
}

/**
 * The rings for a box of `width` × `height`.
 *
 * Coordinates are rounded to a tenth of a pixel: any further precision is
 * bytes nobody can see, and the mockup's own paths are written that way.
 */
export function contourRings(width: number, height: number): ContourRing[] {
  const cx = width * FOCUS_X
  const cy = height * FOCUS_Y
  const out: ContourRing[] = []

  for (let ring = 0; ring < RINGS; ring += 1) {
    const rx = width * FIRST * GROWTH ** ring
    const ry = rx / FLATTEN
    const wobble = wobbleOf(ring)
    const parts: string[] = []

    for (let point = 0; point < POINTS; point += 1) {
      const angle = (point / POINTS) * Math.PI * 2
      const scale = 1 + wobble * noise(ring, angle)
      const x = cx + Math.cos(angle) * rx * scale
      const y = cy + Math.sin(angle) * ry * scale
      parts.push(`${point === 0 ? 'M' : 'L'}${round(x)} ${round(y)}`)
    }

    out.push({ d: `${parts.join('')}Z`, opacity: Math.round((0.9 - 0.05 * ring) * 100) / 100 })
  }

  return out
}

const round = (value: number): number => Math.round(value * 10) / 10
